// Port of webapp/src/components/cardDetail/cardDetailContents.tsx and the
// content elements in components/content/*Element.tsx.
//
// Deviation: upstream mounts a draft-js rich-text editor per text block. The
// clone stores the same block model but edits plain text, so markdown is shown
// literally rather than rendered. See README "未复刻".

import {ReactElement} from 'react'
import classnames from 'classnames'
import {Block} from '../../types/block'
import {Card} from '../../types/card'
import {Board} from '../../types/board'
import {ContentBlockTypes} from '../../types/block'
import * as mutator from '../../state/mutator'
import {Utils, IDType} from '../../common/utils'
import {Icon, IconName} from '../common/icons'
import {ButtonWithMenu, Menu, MenuItem} from '../common/menu'
import {InlineTextInput} from './propertyEditors'

type Props = {
    board: Board
    card: Card
    blocks: Block[]
    readOnly: boolean
}

const blockIcon: Record<string, IconName> = {
    text: 'text',
    h1: 'title',
    h2: 'title',
    h3: 'title',
    checkbox: 'checkbox',
    divider: 'divider',
    'list-item': 'divider',
    image: 'image',
    attachment: 'attachment',
    quote: 'message',
}

const addableTypes: Array<{type: ContentBlockTypes, label: string}> = [
    {type: 'text', label: '文本'},
    {type: 'h1', label: '一级标题'},
    {type: 'h2', label: '二级标题'},
    {type: 'h3', label: '三级标题'},
    {type: 'checkbox', label: '待办事项'},
    {type: 'list-item', label: '无序列表'},
    {type: 'divider', label: '分割线'},
]

export default function CardDetailContents({board, card, blocks, readOnly}: Props): ReactElement {
    const addBlock = (type: ContentBlockTypes): void => {
        const block: Block = {
            id: Utils.createGuid(IDType.BlockID),
            boardId: board.id,
            parentId: card.id,
            createdBy: '',
            modifiedBy: '',
            schema: 1,
            type,
            title: '',
            fields: type === 'checkbox' ? {value: false} : {},
            createAt: Date.now(),
            updateAt: Date.now(),
            deleteAt: 0,
        }
        void mutator.createContentBlock(board.id, card, block)
    }

    return (
        <div className='CardDetailContents w-full'>
            {blocks.map((block) => (
                <ContentElement
                    key={block.id}
                    board={board}
                    card={card}
                    block={block}
                    readOnly={readOnly}
                />
            ))}

            {!readOnly && (
                <ButtonWithMenu
                    button={
                        <span className='mt-1 flex cursor-pointer items-center gap-2 text-sm opacity-60 hover:opacity-100'>
                            <Icon name='add' size={14}/>
                            添加内容
                        </span>
                    }
                >
                    <Menu ariaLabel='添加内容'>
                        {addableTypes.map((item) => (
                            <MenuItem
                                key={item.type}
                                icon={blockIcon[item.type] || 'text'}
                                onClick={() => addBlock(item.type)}
                            >
                                {item.label}
                            </MenuItem>
                        ))}
                    </Menu>
                </ButtonWithMenu>
            )}
        </div>
    )
}

function ContentElement({board, card, block, readOnly}: {board: Board, card: Card, block: Block, readOnly: boolean}): ReactElement {
    const commit = (title: string): void => {
        void mutator.changeBlockTitle(board.id, block, title)
    }

    const wrap = (children: ReactElement): ReactElement => (
        <div className='group flex items-start gap-2'>
            {children}
            {!readOnly && (
                <button
                    type='button'
                    className='IconButton mt-1 opacity-0 group-hover:opacity-100'
                    aria-label='删除内容'
                    onClick={() => void mutator.deleteContentBlock(board.id, card, block)}
                >
                    <Icon name='trash' size={13}/>
                </button>
            )}
        </div>
    )

    switch (block.type) {
    case 'divider':
        return wrap(<div className='my-3 h-px w-full bg-main-fg/10'/>)
    case 'checkbox':
        return wrap(
            <label className='flex flex-1 items-start gap-2'>
                <input
                    type='checkbox'
                    className='mt-1.5'
                    checked={Boolean(block.fields.value)}
                    disabled={readOnly}
                    onChange={(event) => void mutator.changeCheckboxValue(board.id, block, event.target.checked)}
                />
                <span className={classnames('flex-1 text-sm', Boolean(block.fields.value) && 'line-through opacity-50')}>
                    {block.title || (readOnly ? '' : '待办事项')}
                </span>
            </label>,
        )
    case 'h1':
    case 'h2':
    case 'h3': {
        const size = block.type === 'h1' ? 'text-xl' : block.type === 'h2' ? 'text-lg' : 'text-base'
        return wrap(
            <InlineTextInput
                className={classnames(size, 'font-bold')}
                value={block.title}
                onCommit={commit}
                placeholder={`标题 ${block.type.slice(1)}`}
            />,
        )
    }
    case 'list-item':
        return wrap(
            <span className='flex flex-1 items-start gap-2 text-sm'>
                <span className='mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-current opacity-50' aria-hidden={true}/>
                <InlineTextInput className='flex-1' value={block.title} onCommit={commit} placeholder='列表项'/>
            </span>,
        )
    case 'image':
        return wrap(<div className='flex-1 rounded bg-main-fg/5 p-6 text-center text-xs opacity-60'>图片块（未实现文件上传）</div>)
    case 'attachment':
        return wrap(<div className='flex-1 rounded bg-main-fg/5 p-3 text-xs opacity-60'>附件：{block.title || '未命名文件'}</div>)
    default:
        return wrap(<InlineTextInput multiline={true} className='flex-1 text-sm' value={block.title} onCommit={commit} placeholder='文本'/>
        )
    }
}
